// src/pages/NewMessage.tsx
import { useState, useEffect, useRef } from "react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { useStaff } from "../lib/StaffContext";
import { resolveTemplateSenderName } from "../lib/templateMessageIdentity.mjs";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";


function renderPreview(templateBody: string, placeholders: string[], variables: any) {
  let rendered = templateBody;

  placeholders.forEach((key, index) => {
    const value = variables[key] || `{{${key}}}`;
    rendered = rendered.replace(`{{${index + 1}}}`, value);  // Twilio: {{1}}, {{2}}...
    rendered = rendered.replace(`{{${key}}}`, value);         // Chakra: {{patient_name}}...
  });

  return rendered;
}

export default function NewMessage() {
  const user = useUser();
  const { activeStaff } = useStaff();

  const location = useLocation();
  const locationPrefillAppliedRef = useRef(false);

  // Active doctor (from SelectActiveDoctor.tsx)
  const activeDoctor = JSON.parse(localStorage.getItem("activeDoctor") || "null");

  const [activeTab, setActiveTab] = useState<"single" | "mass">("single");
  const [templates, setTemplates] = useState<any[]>([]);
  const [recentPatients, setRecentPatients] = useState<any[]>([]);
  const [directoryPatients, setDirectoryPatients] = useState<any[]>([]);
  const [selectedPatient, setSelectedPatient] = useState("");
  const [directoryInput, setDirectoryInput] = useState("");
  const [toNumber, setToNumber] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [variables, setVariables] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);

  // Auto-filled values
  const [autoStaffName, setAutoStaffName] = useState("");
  const [autoClinicName, setAutoClinicName] = useState("");

  // Mass send state
  const [excelRows, setExcelRows] = useState<any[]>([]);
  const [fileName, setFileName] = useState("");


  /* ------------------------------------------------------------------
     LOAD STAFF NAME (FROM activeDoctor LOCALSTORAGE)
  ------------------------------------------------------------------ */
  useEffect(() => {
    setAutoStaffName(resolveTemplateSenderName(activeStaff, activeDoctor, user));
  }, [activeDoctor, activeStaff, user]);

  /* ------------------------------------------------------------------
     LOAD CLINIC NAME FROM clients.sub_account_twilio
  ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadClinicName() {
      if (!user) return;

      const { data } = await supabase
        .from("clients")
        .select("sub_account_twilio")
        .eq("id", user.id)
        .single();

      if (data?.sub_account_twilio) {
        setAutoClinicName(data.sub_account_twilio);
      }
    }
    loadClinicName();
  }, [user]);

  /* ------------------------------------------------------------------
     LOAD TEMPLATES
  ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadTemplates() {
      if (!user) return;
      const { data } = await supabase
        .from("clinic_templates")
        .select("*")
        .eq("praticien_id", user.id)
        .eq("template_kind", "whatsapp");

      if (data) setTemplates(data);
    }
    loadTemplates();
  }, [user]);

  /* ------------------------------------------------------------------
     LOAD RECENT PATIENTS FROM CONVERSATIONS
  ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadRecentPatients() {
      if (!user) return;

      const { data } = await supabase
        .from("conversations")
        .select("utilisateur, profile_name, created_at")
        .eq("praticien_id", user.id)
        .order("created_at", { ascending: false });

      const rows = data || [];

      const uniques = Array.from(new Set(rows.map((r) => r.utilisateur)));

      const list = uniques.map((num) => {
        const namedRow = rows.find(
          (r) =>
            r.utilisateur === num &&
            r.profile_name &&
            r.profile_name.trim().toLowerCase() !== "not available"
        );

        const displayName = namedRow
          ? namedRow.profile_name.trim()
          : num.replace(/^whatsapp:/, "");

        return { utilisateur: num, displayName };
      });

      setRecentPatients(list);
    }

    loadRecentPatients();
  }, [user]);

  /* ------------------------------------------------------------------
     LOAD PATIENT DIRECTORY
  ------------------------------------------------------------------ */
  useEffect(() => {
    async function loadDirectoryPatients() {
      if (!user) return;

      const { data } = await supabase
        .from("patients_directory")
        .select("id, full_name, phone")
        .eq("praticien_id", user.id)
        .order("full_name", { ascending: true });

      const rows = data || [];

      const list = rows
        .map((patient) => {
          const phone = (patient.phone || "").replace(/^whatsapp:/, "");
          const name = (patient.full_name || "").trim();

          return {
            utilisateur: patient.phone || "",
            displayName: name ? `${name} (${phone})` : phone,
          };
        })
        .filter((patient) => patient.utilisateur);

      setDirectoryPatients(list);
    }

    loadDirectoryPatients();
  }, [user]);



  useEffect(() => {
    if (locationPrefillAppliedRef.current) return;
    const prefill = (location.state as {
      prefillSingleMessage?: { whatsappNumber?: string; patientName?: string };
    } | null)?.prefillSingleMessage;

    if (!prefill?.whatsappNumber) return;

    setActiveTab("single");
    setToNumber(prefill.whatsappNumber);
    setSelectedPatient("");
    setDirectoryInput(prefill.patientName || "");
    locationPrefillAppliedRef.current = true;
  }, [location.state]);

  /* ------------------------------------------------------------------
     INJECT AUTO-VARIABLES ON TEMPLATE CHANGE
  ------------------------------------------------------------------ */
  const selectedTemplate = templates.find((t) => t.name === templateName);

  useEffect(() => {
    if (!selectedTemplate) return;

    const preset: any = {};

    selectedTemplate.placeholders.forEach((key: string) => {
      if (key === "staff_name") preset[key] = autoStaffName;
      if (key === "clinic_name") preset[key] = autoClinicName;
    });

    setVariables(preset);
  }, [selectedTemplate, autoStaffName, autoClinicName]);

  /* ------------------------------------------------------------------
     SELECT PATIENT
  ------------------------------------------------------------------ */
  const handleSelectPatient = (num: string) => {
    setSelectedPatient(num);
    setToNumber(num.replace(/^whatsapp:/, ""));

    const directoryMatch = directoryPatients.find((p) => p.utilisateur === num);
    setDirectoryInput(directoryMatch ? directoryMatch.displayName : "");
  };

  /* ------------------------------------------------------------------
     SEND INDIVIDUAL MESSAGE
  ------------------------------------------------------------------ */
  const handleSend = async () => {
    if (!toNumber || !templateName) {
      alert("Please enter a number and choose a template");
      return;
    }
    setLoading(true);
    const senderName = resolveTemplateSenderName(activeStaff, activeDoctor, user);

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/send-template-message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to_number: toNumber.startsWith("whatsapp:")
            ? toNumber
            : `whatsapp:${toNumber}`,
          template_name: templateName,
          variables,
          praticien_id: user?.id,
          sender_name: senderName,
        }),
      });

      const json = await res.json();
      if (res.ok) alert("Message sent ✅");
      else alert("Erreur : " + json.detail);
    } catch (e) {
      console.error(e);
      alert("Erreur d’envoi");
    }

    setLoading(false);
  };

  /* ------------------------------------------------------------------
     UPLOAD EXCEL
  ------------------------------------------------------------------ */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target?.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      setExcelRows(XLSX.utils.sheet_to_json(ws));
    };
    reader.readAsBinaryString(file);
  };

  /* ------------------------------------------------------------------
     SEND MASS MESSAGES
  ------------------------------------------------------------------ */
  const handleMassSend = async () => {
    if (!templateName) {
      alert("Please choose a template");
      return;
    }
    if (!excelRows.length) {
      alert("Please import an excel file");
      return;
    }

    if (!confirm(`Do you want to send ${excelRows.length} messages ?`)) return;

    setLoading(true);

    let success = 0;
    const senderName = resolveTemplateSenderName(activeStaff, activeDoctor, user);

    for (const row of excelRows) {
      try {
        const vars = { ...row };
        delete vars.whatsapp_number;

        await fetch(`${import.meta.env.VITE_API_URL}/send-template-message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to_number: `whatsapp:${row.whatsapp_number}`,
            template_name: templateName,
            variables: vars,
            praticien_id: user?.id,
            sender_name: senderName,
          }),
        });

        success++;
      } catch (err) {
        console.error("Erreur envoi pour", row.whatsapp_number, err);
      }
    }

    setLoading(false);
    alert(`✅ ${success} messages sent`);
  };


  /* ------------------------------------------------------------------
     RENDER
  ------------------------------------------------------------------ */
  return (
    <div className="p-6 max-w-3xl mx-auto bg-white rounded-lg shadow-sm">
      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "single"
              ? "border-b-2 border-slate-900 text-slate-900"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("single")}
        >
          Single message
        </button>

        <button
          className={`px-4 py-2 font-medium ${
            activeTab === "mass"
              ? "border-b-2 border-slate-900 text-slate-900"
              : "text-gray-500"
          }`}
          onClick={() => setActiveTab("mass")}
        >
          Mass send
        </button>

      </div>

      {/* ----- SINGLE MESSAGE ----- */}
      {activeTab === "single" && (
        <div className="space-y-5">
          {/* Patient selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Select a patient</label>
            <select
              value={selectedPatient}
              onChange={(e) => handleSelectPatient(e.target.value)}
              className="w-full border rounded-lg p-2"
            >
              <option value="">-- Choose from recent conversations --</option>
              {recentPatients.map((p) => (
                <option key={p.utilisateur} value={p.utilisateur}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Patient directory</label>
            <input
              type="text"
              list="patient-directory-options"
              placeholder="Search and select from patient directory"
              value={directoryInput}
              onChange={(e) => {
                const value = e.target.value;
                setDirectoryInput(value);

                const selectedDirectoryPatient = directoryPatients.find(
                  (patient) => patient.displayName === value
                );

                if (selectedDirectoryPatient) {
                  handleSelectPatient(selectedDirectoryPatient.utilisateur);
                } else {
                  setSelectedPatient("");
                }
              }}
              className="w-full border rounded-lg p-2"
            />
            <datalist id="patient-directory-options">
              {directoryPatients.map((p) => (
                <option key={`directory-${p.utilisateur}`} value={p.displayName} />
              ))}
            </datalist>
          </div>

          {/* Manual number */}
          <div>
            <label className="block text-sm font-medium mb-1">Or enter a WhatsApp number</label>
            <input
              type="text"
              placeholder="+6591234567"
              value={toNumber}
              onChange={(e) => {
                setToNumber(e.target.value);
                setSelectedPatient("");
                setDirectoryInput("");
              }}
              className="w-full border rounded-lg p-2"
            />
          </div>

          {/* Template selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Template</label>
            <select
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                setVariables({});
              }}
              className="w-full border rounded-lg p-2"
            >
              <option value="">-- Choose a template --</option>
              {templates.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Template variables */}
          {selectedTemplate && Array.isArray(selectedTemplate.placeholders) && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500">Variables :</p>

              {selectedTemplate.placeholders.map((key: string) => (
                <div key={key}>
                  <label className="block text-xs text-gray-600">{key}</label>

                  <input
                    type="text"
                    value={variables[key] || ""}
                    readOnly={key === "staff_name" || key === "clinic_name"}
                    onChange={(e) =>
                      setVariables({ ...variables, [key]: e.target.value })
                    }
                    className={`mt-1 w-full border rounded-lg p-2 ${
                      key === "staff_name" || key === "clinic_name"
                        ? "bg-gray-100 cursor-not-allowed text-gray-500"
                        : ""
                    }`}
                  />
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleSend}
            disabled={loading}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 w-full"
          >
            {loading ? "Sending..." : "Send message"}
          </button>
        </div>
      )}

      {/* Preview */}
      {selectedTemplate && selectedTemplate.template_body && (
        <div className="mt-4">
          <p className="text-sm text-gray-500 mb-1">Preview :</p>
      
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm whitespace-pre-wrap leading-relaxed text-gray-700 shadow-sm">
            {renderPreview(
              selectedTemplate.template_body,
              selectedTemplate.placeholders,
              variables
            )}
          </div>
        </div>
      )}

      {/* ----- MASS SEND ----- */}
      {activeTab === "mass" && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium mb-1">Template to use</label>
            <select
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full border rounded-lg p-2"
            >
              <option value="">-- Choose a template --</option>
              {templates.map((t) => (
                <option key={t.id} value={t.name}>
                  {t.display_name}
                </option>
              ))}
            </select>
          </div>

          {/* Excel input */}
          <div>
            <label className="block text-sm font-medium mb-1">Upload Excel file</label>
            <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
            {fileName && <p className="text-sm text-gray-600 mt-1">📄 {fileName}</p>}
          </div>

          {/* Excel preview */}
          {excelRows.length > 0 && (
            <div className="overflow-x-auto border rounded-lg p-2 max-h-64">
              <table className="min-w-full text-sm">
                <thead className="border-b font-medium bg-gray-50">
                  <tr>
                    {Object.keys(excelRows[0]).map((col) => (
                      <th key={col} className="px-2 py-1 text-left">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {excelRows.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      {Object.values(row).map((val, i) => (
                        <td key={i} className="px-2 py-1">
                          {String(val)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button
            onClick={handleMassSend}
            disabled={loading}
            className="bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 w-full"
          >
            {loading ? "Sending..." : "Send all messages"}
          </button>
        </div>
      )}

    </div>
  );
}
